/**
 * Evaluations Tab Component for HR Payroll Page
 * Displays general performance evaluations
 */
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Star, Calendar, Plus, Upload, Trash2, File } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export function EvaluationsTab({
  evaluations,
  loading,
  onAddClick,
  onDelete,
  onUploadDocument,
  uploadingDoc,
  selectedFile,
  setSelectedFile,
  getEmployeeName,
  getAverageRating,
  getRatingColor
}) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="evaluations-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Performance Evaluations</h2>
          <p className="text-sm text-slate-500">Track and manage employee performance reviews</p>
        </div>
        <Button onClick={onAddClick} className="gap-2" data-testid="add-evaluation-btn">
          <Plus className="w-4 h-4" />
          New Evaluation
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="spinner w-8 h-8 mx-auto"></div>
        </div>
      ) : evaluations.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          <Star className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p className="font-medium">No performance evaluations yet</p>
          <p className="text-sm">Click &quot;New Evaluation&quot; to create one</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {evaluations.map((eval_) => (
            <div
              key={eval_.id}
              className="p-4 border border-slate-200 rounded-lg hover:border-primary/30 transition-colors"
              data-testid={`evaluation-${eval_.id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-slate-800">{getEmployeeName(eval_.employee_id)}</h3>
                  <p className="text-sm text-slate-500">
                    <Calendar className="w-3 h-3 inline mr-1" />
                    {eval_.evaluation_date}
                  </p>
                </div>
                <div className={`px-3 py-1 rounded-full text-sm font-bold ${getRatingColor(getAverageRating(eval_))}`}>
                  {getAverageRating(eval_)} / 5
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
                <div className="flex justify-between">
                  <span className="text-slate-500">Attendance:</span>
                  <span className="font-medium">{eval_.attendance_rating}/5</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Punctuality:</span>
                  <span className="font-medium">{eval_.punctuality_rating}/5</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Quality:</span>
                  <span className="font-medium">{eval_.quality_of_work_rating}/5</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Communication:</span>
                  <span className="font-medium">{eval_.communication_rating}/5</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Teamwork:</span>
                  <span className="font-medium">{eval_.teamwork_rating}/5</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Professionalism:</span>
                  <span className="font-medium">{eval_.professionalism_rating}/5</span>
                </div>
              </div>

              {eval_.strengths && (
                <p className="text-xs text-slate-600 mb-2">
                  <strong>Strengths:</strong> {eval_.strengths.slice(0, 100)}...
                </p>
              )}

              <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                <span className="text-xs text-slate-500">By: {eval_.evaluator_name}</span>
                <div className="flex items-center gap-1">
                  <Input
                    type="file"
                    className="h-7 w-24 text-xs"
                    onChange={(e) => setSelectedFile(e.target.files[0])}
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs"
                    onClick={() => onUploadDocument(eval_.id)}
                    disabled={!selectedFile || uploadingDoc === eval_.id}
                  >
                    <Upload className="w-3 h-3" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 w-7 p-0 text-red-500"
                    onClick={() => onDelete(eval_.id)}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>

              {eval_.documents?.length > 0 && (
                <div className="mt-2 pt-2 border-t border-slate-100">
                  <p className="text-xs text-slate-500 mb-1">Documents:</p>
                  {eval_.documents.map((doc) => (
                    <div key={doc.id} className="flex items-center gap-1 text-xs">
                      <File className="w-3 h-3 text-slate-400" />
                      <a
                        href={`${API}/hr/evaluations/${eval_.id}/documents/${doc.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline truncate"
                      >
                        {doc.filename}
                      </a>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
